import { useRegisterSW } from 'virtual:pwa-register/react';
import toast from 'react-hot-toast';

export function usePWAUpdates(
  options: {
    updateIntervalMs?: number;
    onUpdateAvailable?: () => void;
    onOfflineReady?: () => void;
    enablePeriodicChecks?: boolean;
    useAdaptiveInterval?: boolean;
    checkOnLoad?: boolean;
  } = {}
) {
  const {
    updateIntervalMs = 30 * 60 * 1000, // 30 minutes default
    onUpdateAvailable,
    enablePeriodicChecks = true,
    useAdaptiveInterval = false,
    checkOnLoad = true,
  } = options;

  const getUpdateInterval = () => {
    if (!useAdaptiveInterval) {
      return updateIntervalMs;
    }

    if (document.visibilityState === 'visible') {
      return 15 * 60 * 1000; // 15 minutes when active
    }
    return 60 * 60 * 1000; // 1 hour when inactive
  };

  const checkForUpdates = async (swUrl: string, r: ServiceWorkerRegistration) => {
    // Don't update if a service worker is currently installing
    if (r.installing || !navigator) return;

    // Don't update if the user is offline
    if ('connection' in navigator && !navigator.onLine) return;

    try {
      // Fetch the service worker file to check if it's available
      const resp = await fetch(swUrl, {
        cache: 'no-store',
        headers: {
          cache: 'no-store',
          'cache-control': 'no-cache',
        },
      });

      if (resp?.status === 200) {
        await r.update();
      }
    } catch {
      // Service worker update check failed
    }
  };

  const { needRefresh, offlineReady, updateServiceWorker } = useRegisterSW({
    onNeedRefresh() {
      if (onUpdateAvailable) {
        onUpdateAvailable();
      } else {
        toast.success(
          'New version available! Please refresh the page to load the new version.'
        );
      }
    },
    async onRegisteredSW(swUrl, r) {
      if (!r) return;

      if (checkOnLoad) {
        await checkForUpdates(swUrl, r);
      }

      if (!enablePeriodicChecks) return;

      let intervalId: number | null = null;
      const startPeriodicChecks = () => {
        if (intervalId) {
          clearInterval(intervalId);
        }

        intervalId = setInterval(() => {
          checkForUpdates(swUrl, r);
        }, getUpdateInterval()) as unknown as number;
      };

      startPeriodicChecks();

      if (useAdaptiveInterval) {
        const handleVisibilityChange = () => {
          startPeriodicChecks();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          if (intervalId) {
            clearInterval(intervalId);
          }
        };
      }
    },
  });

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker,
    forceUpdate: () => updateServiceWorker(true),
    isUpdateAvailable: needRefresh,
    isOfflineReady: offlineReady,
  };
}
